import React from 'react';
import classNames from 'classnames';

import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import Menuable from 'ds/Menuable';
import Text from 'ds/Text';
import { Color } from 'shared';

import Circle from './Circle';

export type DotStyle = 'solid' | 'outline';

export interface StatusPillPaletteEntry {
  backgroundColor: Color;
  hoverBackgroundColor: Color;
  activeBackgroundColor: Color;
  dotColor: Color;
  textColor: Color;
  dotStyle: DotStyle;
}

type StatusPillPaletteSpec = {
  bg: Color;
  hoverBg: Color;
  activeBg: Color;
  dot: Color;
  text: Color;
  dotStyle?: DotStyle;
};

function resolvePalette(spec: StatusPillPaletteSpec): StatusPillPaletteEntry {
  return {
    backgroundColor: spec.bg,
    hoverBackgroundColor: spec.hoverBg,
    activeBackgroundColor: spec.activeBg,
    dotColor: spec.dot,
    textColor: spec.text,
    dotStyle: spec.dotStyle ?? 'solid'
  };
}

export type StatusPillPaletteId =
  | 'gray-light'
  | 'gray'
  | 'gold'
  | 'green'
  | 'blue-light'
  | 'blue'
  | 'purple'
  | 'red-light'
  | 'red';

const PALETTE_SPECS: Record<StatusPillPaletteId, StatusPillPaletteSpec> = {
  'gray-light': {
    bg: 'gray-50',
    hoverBg: 'gray-100',
    activeBg: 'gray-200',
    dot: 'gray-400',
    text: 'gray-900',
    dotStyle: 'outline'
  },
  gray: { bg: 'gray-50', hoverBg: 'gray-100', activeBg: 'gray-200', dot: 'gray-400', text: 'gray-900' },
  gold: { bg: 'gold-100', hoverBg: 'gold-200', activeBg: 'gold-300', dot: 'gold-600', text: 'gold-800' },
  green: { bg: 'green-100', hoverBg: 'green-200', activeBg: 'green-300', dot: 'green-600', text: 'green-700' },
  'blue-light': {
    bg: 'blue-100',
    hoverBg: 'blue-200',
    activeBg: 'blue-300',
    dot: 'blue-500',
    text: 'blue-600',
    dotStyle: 'outline'
  },
  blue: { bg: 'blue-100', hoverBg: 'blue-200', activeBg: 'blue-300', dot: 'blue-500', text: 'blue-600' },
  purple: { bg: 'purple-100', hoverBg: 'purple-200', activeBg: 'purple-300', dot: 'purple-600', text: 'purple-800' },
  'red-light': {
    bg: 'red-100',
    hoverBg: 'red-200',
    activeBg: 'red-300',
    dot: 'red-700',
    text: 'red-700',
    dotStyle: 'outline'
  },
  red: { bg: 'red-100', hoverBg: 'red-200', activeBg: 'red-300', dot: 'red-700', text: 'red-700' }
};

const STATUS_PILL_PALETTES: Record<StatusPillPaletteId, StatusPillPaletteEntry> = Object.fromEntries(
  Object.entries(PALETTE_SPECS).map(([id, spec]) => [id, resolvePalette(spec)])
) as Record<StatusPillPaletteId, StatusPillPaletteEntry>;

export function getStatusPillPalette(id: StatusPillPaletteId): StatusPillPaletteEntry {
  return STATUS_PILL_PALETTES[id];
}

export interface StatusPillOption {
  value: string;
  label: string;
  /** Optional palette for this option's status dot in dropdown menus. */
  paletteId?: StatusPillPaletteId;
  [key: string]: unknown;
}

export type StatusPillProps = {
  palette: StatusPillPaletteId;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  showDot?: boolean;
  editable?: boolean;
  active?: boolean;
  options?: StatusPillOption[];
  onOptionSelect?: (option: StatusPillOption) => void;
  /** When set, dropdown open state is controlled (e.g. for single-open-at-a-time). */
  isOpen?: boolean;
  /** Called when dropdown open state should change. Use with isOpen for controlled mode. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
};

const PILL_SIZE_STYLES: Record<
  'sm' | 'md' | 'lg',
  { paddingLeft: number; paddingRight: number; height: number; textSize: 'body-xs' | 'body-sm' }
> = {
  sm: { paddingLeft: 8, paddingRight: 8, height: 24, textSize: 'body-xs' },
  md: { paddingLeft: 8, paddingRight: 8, height: 28, textSize: 'body-xs' },
  lg: { paddingLeft: 12, paddingRight: 12, height: 32, textSize: 'body-sm' }
};

const StatusPill: React.FC<StatusPillProps> = props => {
  const {
    palette,
    label,
    size = 'lg',
    showDot = true,
    editable = false,
    active = false,
    options,
    onOptionSelect,
    isOpen,
    onOpenChange,
    className,
    style
  } = props;

  const hasDropdown = editable && (options?.length ?? 0) > 0 && onOptionSelect;

  const resolved = STATUS_PILL_PALETTES[palette];

  const backgroundColor = resolved.backgroundColor;
  const hoverBackgroundColor = resolved.hoverBackgroundColor;
  const activeBackgroundColor = resolved.activeBackgroundColor;
  const dotColor = resolved.dotColor;
  const textColor = resolved.textColor;
  const dotStyle = resolved.dotStyle;
  const { textSize, ...sizeProps } = PILL_SIZE_STYLES[size];

  const dotFrame = (
    <Layout width={12} height={12} padding={2} align="center" justify="center">
      {dotStyle === 'outline' ? (
        <Layout width={8} height={8} borderRadius="circular" borderColor={dotColor} borderWidth={1} />
      ) : (
        <Circle dimension={8} color={dotColor} />
      )}
    </Layout>
  );

  const renderPill = (activeValue: boolean) => {
    const caretIcon = activeValue ? 'upCaret' : 'downCaret';
    const resolvedBackgroundColor = activeValue ? activeBackgroundColor : backgroundColor;
    const interactiveClasses =
      editable && !activeValue
        ? [`hover-bg-color-${hoverBackgroundColor}`, `active-bg-color-${activeBackgroundColor}`]
        : [];
    const paddingRight = editable ? sizeProps.paddingRight : sizeProps.paddingRight + 4;
    const heightPx = sizeProps.height;
    const pillStyle: React.CSSProperties = {
      ...style,
      height: heightPx,
      minHeight: heightPx,
      maxHeight: heightPx,
      boxSizing: 'border-box'
    };
    return (
      <Layout
        data-cy="status-pill"
        className={classNames('StatusPill', ...interactiveClasses, className)}
        style={pillStyle}
        color={resolvedBackgroundColor}
        paddingLeft={sizeProps.paddingLeft}
        paddingRight={paddingRight}
        borderRadius={1000}
        align="center"
        direction="row"
        justify="center"
        gap={4}
        cursor={editable ? 'pointer' : undefined}
      >
        {showDot ? dotFrame : null}
        <Layout direction="row" align="center" gap={4}>
          <Text size={textSize} color={textColor} semibold wrap={false}>
            {label}
          </Text>
          {editable && <Icon name={caretIcon} size={16} color={dotColor} />}
        </Layout>
      </Layout>
    );
  };

  if (hasDropdown && options) {
    const menuableOptions = options.map(option => {
      const optionPalette = option.paletteId ? getStatusPillPalette(option.paletteId) : resolved;
      const optionDotColor = optionPalette.dotColor;
      const optionDotStyle = optionPalette.dotStyle;

      const optionDotFrame = (
        <Layout width={12} height={12} padding={2} align="center" justify="center">
          {optionDotStyle === 'outline' ? (
            <Layout width={8} height={8} borderRadius="circular" borderColor={optionDotColor} borderWidth={1} />
          ) : (
            <Circle dimension={8} color={optionDotColor} />
          )}
        </Layout>
      );

      const labelNode = (
        <Layout direction="row" align="center" gap={8}>
          {optionDotFrame}
          <Text size="body-xs" color="gray-900" wrap={false} align="left">
            {option.label}
          </Text>
        </Layout>
      );

      return {
        label: labelNode,
        onClick: () => onOptionSelect(option)
      };
    });
    return (
      <Menuable options={menuableOptions} itemTextColor="gray-600" isOpen={isOpen} onOpenChange={onOpenChange}>
        {({ isOpen: menuIsOpen }) => renderPill(menuIsOpen)}
      </Menuable>
    );
  }

  return renderPill(active);
};

export default StatusPill;
